// MyPoliciesPage — every policy, openable, with an honest status on the two
// that carry one.
//
// THE SHAPE OF THIS PAGE IS THE RULING. Ramal Taylor, 2026-09-17/19:
//
//   * TWO acknowledgments, not one and not five. Everything else is reference
//     material — openable, no status, not counted.
//   * A file shows THE VERSION THEY ACKNOWLEDGED, not the current one. The
//     newer version is offered separately, never substituted for it.
//   * "Superseded" and "not covered" are DIFFERENT FACTS and are rendered
//     apart. Collapsing them into "outdated" is the half-satisfied-showing-
//     green mistake in a new place.
//   * A SUSPENDED DEADLINE HAS NO DATE. A paused person cannot acknowledge, so
//     their file reads "deadline suspended" — never a date, because showing one
//     would be a fiction.

import { useState, useEffect, useCallback } from 'react';
import { DocumentTextIcon, CheckCircleIcon, ExclamationTriangleIcon, ArrowTopRightOnSquareIcon, XMarkIcon } from '@heroicons/react/24/outline';
import api from '../../api/client';
import toast from 'react-hot-toast';

const fmt = (d) => (d ? new Date(d).toLocaleDateString('en-US',
  { year: 'numeric', month: 'short', day: 'numeric' }) : '—');

// The five derived states, each with the words that make it true rather than
// a colour that implies something stronger.
const STATE = {
  current:             { label: 'Up to date',          cls: 'bg-green-50 text-green-700 border-green-200' },
  superseded:          { label: 'Newer version',       cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  // A document ADDED to the acknowledgment after you signed is not a newer
  // version of anything — nothing is newer, something is new. Kept apart for
  // the same reason the per-document states are.
  incomplete:          { label: 'Action needed',       cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  overdue:             { label: 'Overdue',             cls: 'bg-red-50 text-red-700 border-red-200' },
  never_acknowledged:  { label: 'Not acknowledged',    cls: 'bg-gray-100 text-gray-600 border-gray-200' },
};

// ─── THE DOCUMENT IS READ WHERE IT IS ACKNOWLEDGED ──────────────────
//
// Ruled 2026-09-19 after the tab approach produced an orphan about:blank AND
// navigated the portal away. The cause was mine: window.open(url, name,
// 'noopener') RETURNS NULL BY SPECIFICATION, so the handle was null, the blank
// tab stayed, and the fallback navigated the current window. The "safety" flag
// caused the defect.
//
// The fix is not a better tab.
//
//   STAFF READ THESE ON PHONES. A blank tab plus a replaced portal page, on a
//   phone, is how twenty people decide the portal is broken.
//
// The job is: read a document, then tick that you read it. That belongs in ONE
// place. So the PDF renders IN the page, with the signature underneath it, and
// nobody leaves the page they are acknowledging from. No new tab, no popup
// blocker, identical on phone and laptop.
//
// ─── iOS SAFARI RENDERS ONLY THE FIRST PAGE OF AN IFRAMED PDF ───────
//
// Known, handled rather than discovered. Every viewer carries an explicit
// "Open full document" link, always visible and not hidden behind a
// user-agent test — sniffing gets it wrong for somebody, and a link that is
// always there costs nothing and cannot be wrong.
//
// That link is a PLAIN ANCHOR and it works, where the original could not: the
// presigned URL carries its own credentials in the query string, so it needs
// no Authorization header. The two-step is only needed to OBTAIN it.
function PolicyViewerModal({ ack, documents, initialDocId, expectedSignature, onClose, onAcknowledged }) {
  const [docId, setDocId] = useState(initialDocId || documents[0]?.document_id);
  const [url, setUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [signature, setSignature] = useState('');
  const [busy, setBusy] = useState(false);

  const doc = documents.find((d) => d.document_id === docId) || documents[0];
  const versionId = doc?.current_version_id;

  // ─── WHY THE IFRAME DOES NOT POINT AT THE ROUTE DIRECTLY ────────
  //
  // AN IFRAME src IS A NAVIGATION. It carries cookies, not headers, and this
  // app's token lives in localStorage and is attached by an axios
  // interceptor — so <iframe src="/api/policies/versions/23/view"> would 401,
  // which is the exact defect that broke the title link twice.
  //
  // So the bytes are fetched THROUGH the authenticated call and handed to the
  // frame as a blob: URL. A blob: URL inherits this page's origin, so the
  // frame is same-origin and nothing — not Chrome, not a privacy extension —
  // can refuse it. The server still streams; only the browser holds a copy,
  // which it must in order to render one.
  //
  // Every benefit of serving from our own origin survives: same origin, no
  // presigned URL anywhere in the browser, identical on a phone, and the
  // full-document link uses this same blob so both paths agree.
  useEffect(() => {
    let live = true;
    let objectUrl = null;
    setUrl(null); setLoading(true);
    if (!versionId) { setLoading(false); return undefined; }
    api.get(`/policies/versions/${versionId}/view`, { responseType: 'blob' })
      .then(({ data }) => {
        if (!live) return;
        objectUrl = URL.createObjectURL(
          data.type ? data : new Blob([data], { type: 'application/pdf' })
        );
        setUrl(objectUrl);
      })
      .catch(async (err) => {
        if (!live) return;
        // An error body arrives as a Blob because of responseType, so the
        // server's message has to be read back out rather than guessed at.
        let msg = 'Could not open that document.';
        try {
          const text = err?.response?.data instanceof Blob
            ? await err.response.data.text() : null;
          if (text) msg = JSON.parse(text).error || msg;
        } catch { /* keep the default */ }
        toast.error(msg);
      })
      .finally(() => { if (live) setLoading(false); });
    return () => {
      live = false;
      // Revoke on close and on document switch, or every PDF a person opens
      // stays in memory until the tab is closed.
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [versionId]);

  const needsAck = ack && ack.status !== 'current';

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/my/acknowledgments/${ack.key}`, { signature });
      toast.success(`Acknowledged ${data.acknowledgment}.`);
      onAcknowledged();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Could not save your acknowledgment.');
    } finally { setBusy(false); }
  };

  return (
    // Full screen on a phone; inset on a laptop. One layout, one code path.
    <div className="fixed inset-0 z-50 bg-black/50 flex items-stretch sm:items-center justify-center sm:p-6"
      onClick={onClose}>
      <div className="bg-white w-full sm:max-w-3xl sm:rounded-2xl flex flex-col h-full sm:h-[90vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}>

        <div className="px-4 py-3 border-b border-gray-200 flex items-start justify-between gap-3 flex-shrink-0">
          <div className="min-w-0">
            <h2 className="text-sm font-semibold text-gray-900 truncate">{doc?.title}</h2>
            {doc?.current_version_label && (
              <p className="text-[11px] text-gray-500">Version {doc.current_version_label}</p>
            )}
          </div>
          <button onClick={onClose} aria-label="Close"
            className="text-gray-400 hover:text-gray-600 flex-shrink-0 p-1 -m-1">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Several documents under one acknowledgment: switch between them
            without leaving. Scrolls sideways rather than wrapping, so it
            cannot eat the viewer's height on a narrow screen. */}
        {documents.length > 1 && (
          <div className="flex gap-1 px-3 py-2 border-b border-gray-100 overflow-x-auto flex-shrink-0">
            {documents.map((d) => (
              <button key={d.document_id} onClick={() => setDocId(d.document_id)}
                className={`text-[11px] px-2.5 py-1 rounded-full border whitespace-nowrap ${
                  d.document_id === docId
                    ? 'bg-orange-500 text-white border-orange-500'
                    : 'bg-white text-gray-600 border-gray-200'}`}>
                {d.title}
              </button>
            ))}
          </div>
        )}

        <div className="flex-1 min-h-0 bg-gray-100">
          {loading && <div className="h-full flex items-center justify-center text-sm text-gray-500">Loading…</div>}
          {!loading && !url && (
            <div className="h-full flex items-center justify-center text-sm text-gray-500 p-6 text-center">
              This document has not been published yet.
            </div>
          )}
          {!loading && url && (
            // ─── PDF VIEWER PARAMETERS, ON THE FRAME ONLY ───────────
            //
            // Chrome opened the document at 27% with its thumbnail sidebar
            // showing, which in a narrow modal ate most of the width and on a
            // phone left almost nothing. Unreadable until the person zoomed,
            // on a page whose whole purpose is reading.
            //
            //   navpanes=0  hides the thumbnail sidebar
            //   view=FitH   fits the page to the frame width, so it opens
            //               readable instead of needing a pinch
            //   toolbar=0   hides Chrome's toolbar
            //
            // THE FRAGMENT IS ON THE IFRAME AND NOWHERE ELSE. "Open full
            // document" below deliberately uses the BARE url, so that view
            // keeps Chrome's toolbar and with it PRINT AND DOWNLOAD. Hiding
            // the toolbar here is only acceptable because saving and printing
            // survive one click away — nobody should lose the ability to keep
            // a copy of a policy they are being asked to agree to.
            //
            // Chrome/Edge parameters. Viewers that do not support them ignore
            // the fragment, so nothing breaks where they are not honoured.
            <iframe src={`${url}#toolbar=0&navpanes=0&view=FitH`}
              title={doc?.title} className="w-full h-full border-0" />
          )}
        </div>

        <div className="px-4 py-3 border-t border-gray-200 flex-shrink-0 space-y-3">
          {url && (
            // BARE url on purpose — see the iframe note. This is the path
            // that keeps print and download.
            <a href={url} target="_blank" rel="noreferrer"
              className="text-xs text-orange-600 hover:underline inline-flex items-center gap-1">
              Open full document <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5" />
            </a>
          )}
          {url && (
            <p className="text-[11px] text-gray-500">
              Use “Open full document” to print or save it, or if the preview
              above shows only the first page — some iPhones do. The document
              is served by the portal, never from an outside site.
            </p>
          )}

          {needsAck && (
            <form onSubmit={submit} className="space-y-2 pt-1 border-t border-gray-100">
              <p className="text-[11px] text-gray-600 pt-2">
                By signing you confirm you have read
                {documents.length > 1 ? ` all ${documents.length} documents above` : ' this document'},
                at the versions shown.
              </p>
              <input value={signature} onChange={(e) => setSignature(e.target.value)}
                placeholder={expectedSignature || 'Type your full name'}
                autoComplete="off"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-base sm:text-sm" />
              <button type="submit" disabled={busy}
                className="w-full sm:w-auto text-sm font-semibold text-white bg-orange-500 hover:bg-orange-600 disabled:opacity-50 px-5 py-2.5 rounded-xl">
                {busy ? 'Saving…' : 'I have read this — acknowledge'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

function AcknowledgmentCard({ ack, onChanged }) {
  // null = closed; otherwise the document_id to open at.
  const [viewerDoc, setViewerDoc] = useState(null);
  const meta = STATE[ack.status] || STATE.never_acknowledged;

  const unpublished = ack.documents.some((d) => !d.current_version_label);

  return (
    <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
      <div className="px-5 py-4 border-b border-gray-100 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-sm font-semibold text-gray-900">{ack.title}</h2>
          {ack.acknowledged_at ? (
            <p className="text-xs text-gray-500 mt-0.5">
              You acknowledged this on {fmt(ack.acknowledged_at)}.
            </p>
          ) : (
            <p className="text-xs text-gray-500 mt-0.5">You have not acknowledged this yet.</p>
          )}
        </div>
        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border flex-shrink-0 ${meta.cls}`}>
          {meta.label}
        </span>
      </div>

      <ul className="divide-y divide-gray-50">
        {ack.documents.map((d) => (
          <li key={d.document_id} className="px-5 py-3 flex items-start gap-3">
            <DocumentTextIcon className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              {d.current_version_id ? (
                <button type="button" onClick={() => setViewerDoc(d.document_id)}
                  className="text-sm text-orange-700 hover:underline font-medium text-left">
                  {d.title} <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5 inline -mt-0.5" />
                </button>
              ) : (
                <p className="text-sm text-gray-900">{d.title}</p>
              )}

              {/* WHAT THEY AGREED TO, FIRST AND ALWAYS — and openable, even
                  once it has been superseded. A record saying "I have read
                  this" is worthless if the thing can never be seen again. */}
              {d.acknowledged_version_label && (
                <p className="text-xs text-gray-600 mt-0.5 inline-flex items-center gap-1">
                  <CheckCircleIcon className="w-3.5 h-3.5 text-green-600" />
                  You acknowledged{' '}
                  {d.acknowledged_version_id && d.acknowledged_version_id !== d.current_version_id ? (
                    <button type="button"
                      onClick={() => setViewerDoc(d.document_id)}
                      className="font-semibold text-orange-700 hover:underline">
                      {d.acknowledged_version_label}
                    </button>
                  ) : (
                    <span className="font-semibold">{d.acknowledged_version_label}</span>
                  )}
                </p>
              )}

              {/* NEVER ACKNOWLEDGED — no record at all. This used to render as
                  "Added after you signed", which told somebody who had signed
                  nothing that they had signed something. */}
              {d.state === 'never_acknowledged' && (
                <p className="text-xs text-gray-500 mt-0.5">
                  {d.current_version_label
                    ? <>{d.current_version_label} — not yet acknowledged</>
                    : <>Not published yet</>}
                </p>
              )}

              {/* SUPERSEDED — a revision to something they signed. */}
              {d.state === 'superseded' && (
                <p className="text-xs text-amber-700 mt-0.5">
                  {d.current_version_label} published {fmt(d.current_published_at)} — not yet acknowledged
                </p>
              )}

              {/* NOT COVERED — a DIFFERENT fact: a record exists and this
                  document was added to the acknowledgment afterwards. */}
              {d.state === 'not_covered' && (
                <p className="text-xs text-gray-500 mt-0.5">
                  {d.current_version_label
                    ? <>Added to this acknowledgment after you signed — never acknowledged</>
                    : <>Not published yet</>}
                </p>
              )}
            </div>
          </li>
        ))}
      </ul>

      <div className="px-5 py-3 bg-gray-50/60 border-t border-gray-100">
        {/* A SUSPENDED DEADLINE HAS NO DATE. */}
        {ack.obligation?.suspended && (
          <p className="text-xs text-gray-600">
            Your account is paused — <span className="font-semibold">deadline suspended</span>.
            The clock restarts when your access does.
          </p>
        )}
        {ack.obligation && !ack.obligation.suspended && (
          <p className={`text-xs ${ack.status === 'overdue' ? 'text-red-700 font-semibold' : 'text-amber-700'}`}>
            {ack.status === 'overdue' ? <ExclamationTriangleIcon className="w-3.5 h-3.5 inline mr-1" /> : null}
            {ack.status === 'overdue' ? 'Was due' : 'Due'} {fmt(ack.obligation.due_at)}
          </p>
        )}

        {unpublished ? (
          <p className="text-xs text-gray-500 mt-1">
            Not everything here has been published yet. Nothing is outstanding on your side right now.
          </p>
        ) : ack.status === 'current' ? (
          <p className="text-xs text-gray-500 mt-1">Nothing outstanding.</p>
        ) : (
          // ONE BUTTON, ONE PLACE. It opens the reader; the signature sits
          // under the document there. You cannot reach "I have read this"
          // without the document being on screen.
          <button onClick={() => setViewerDoc(ack.documents[0]?.document_id)}
            className="mt-1 w-full sm:w-auto text-sm font-semibold text-white bg-orange-500 hover:bg-orange-600 px-4 py-2.5 sm:py-1.5 rounded-xl">
            {ack.acknowledged_at ? 'Read and acknowledge the new version' : 'Read and acknowledge'}
          </button>
        )}
      </div>

      {viewerDoc !== null && (
        <PolicyViewerModal
          ack={ack}
          documents={ack.documents}
          initialDocId={viewerDoc}
          expectedSignature={ack.expected_signature}
          onClose={() => setViewerDoc(null)}
          onAcknowledged={onChanged}
        />
      )}
    </div>
  );
}

export default function MyPoliciesPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // Reference documents open in the same reader, with no signature section —
  // one viewer, one behaviour, whether or not the document carries a status.
  const [refDoc, setRefDoc] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/policies');
      setData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not load policies.');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="p-8 text-sm text-gray-500">Loading…</div>;
  if (!data) return null;

  const reference = data.documents.filter((d) => !d.acknowledgment_key);

  return (
    <div className="animate-slide-up max-w-3xl mx-auto space-y-5">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Policies</h1>
        <p className="text-sm text-gray-500 mt-0.5">
          The version you acknowledged stays on your file permanently. If a policy is
          revised you will be asked to acknowledge the new one — until you do, the
          version you signed is the one that applies to you.
        </p>
      </div>

      {data.acknowledgments.map((a) => (
        <AcknowledgmentCard key={a.key} ack={a} onChanged={load} />
      ))}

      {refDoc && (
        <PolicyViewerModal
          ack={null}
          documents={[{
            document_id: refDoc.id,
            title: refDoc.title,
            current_version_id: refDoc.current_version_id,
            current_version_label: refDoc.version_label,
          }]}
          initialDocId={refDoc.id}
          onClose={() => setRefDoc(null)}
          onAcknowledged={() => {}}
        />
      )}

      {reference.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
          <div className="px-5 py-3 border-b border-gray-100 bg-gray-50/50">
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Reference</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              For information. No acknowledgment needed.
            </p>
          </div>
          <ul className="divide-y divide-gray-50">
            {reference.map((d) => (
              <li key={d.key} className="px-5 py-3 flex items-center gap-3">
                <DocumentTextIcon className="w-4 h-4 text-gray-400" />
                {d.current_version_id ? (
                  <button type="button" onClick={() => setRefDoc(d)}
                    className="text-sm text-orange-700 hover:underline flex-1 text-left">
                    {d.title} <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5 inline -mt-0.5" />
                  </button>
                ) : (
                  <span className="text-sm text-gray-900 flex-1">{d.title}</span>
                )}
                <span className="text-xs text-gray-500">
                  {d.published ? d.version_label : 'Not published yet'}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
